import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import {
  User,
  Briefcase,
  Wallet,
  FileCheck,
  CheckCircle2,
  ArrowLeft,
  ArrowRight,
  Upload,
  Calendar,
  Building2,
  CreditCard,
  Phone,
  Mail,
  MapPin,
  Check,
  AlertCircle,
  FileText,
  Link2,
} from "lucide-react";
import { message, Upload as AntUpload, Button } from "antd";
import { useTheme } from "../context/ThemeContext";
import { fintechApi, leadsApi } from "../services/api";

const STEPS = [
  { step: 1, title: "Applicant Details", icon: User },
  { step: 2, title: "Employment & Income", icon: Briefcase },
  { step: 3, title: "Loan & Live EMI", icon: Wallet },
  { step: 4, title: "Document Vault", icon: FileCheck },
  { step: 5, title: "Review & Submit", icon: CheckCircle2 },
];

const LOAN_TYPES = [
  { id: "PERSONAL", label: "Personal Loan", defaultRate: 12.5 },
  { id: "BUSINESS", label: "Business Loan", defaultRate: 14.0 },
  { id: "HOME", label: "Home Loan", defaultRate: 8.75 },
  { id: "VEHICLE", label: "Vehicle Loan", defaultRate: 9.5 },
  { id: "EDUCATION", label: "Education Loan", defaultRate: 10.0 },
  { id: "GOLD", label: "Gold Loan", defaultRate: 11.0 },
  { id: "BNPL", label: "BNPL / Consumer Durable", defaultRate: 16.0 },
  { id: "MSME", label: "MSME Secured Loan", defaultRate: 13.5 },
];

// key = form state key, type = backend documentType
const DOCUMENT_TYPES = [
  { key: "pan", type: "PAN", title: "PAN Card Document", desc: "Front copy of applicant PAN" },
  { key: "aadhaar", type: "AADHAAR", title: "Aadhaar Card", desc: "Front & Back combined PDF/Image" },
  { key: "incomeProof", type: "INCOME_PROOF", title: "Income Proof / Salary Slip", desc: "Last 3 months salary slips or ITR" },
  { key: "bankStatement", type: "BANK_STATEMENT", title: "Bank Statement", desc: "Last 6 months bank statement PDF" },
  { key: "photo", type: "PHOTO", title: "Applicant Passport Photo", desc: "Recent passport sized photo" },
];

const STANDARD_TENURES = [6, 12, 24, 36, 48, 60, 120, 240];

const PHONE_REGEX = /^[6-9]\d{9}$/;
const PAN_REGEX = /^[A-Z]{5}\d{4}[A-Z]$/;
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;

const formatINR = (val) => {
  if (val === undefined || val === null || isNaN(val)) return "₹0";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(val);
};

// Pehli non-empty value string me — backend number ya null bheje to bhi .trim() safe rahe
const str = (...vals) => {
  const v = vals.find((x) => x !== undefined && x !== null && x !== "");
  return v === undefined ? "" : String(v);
};

// Lead me number +91 / spaces ke saath ho sakta hai — last 10 digits
const toTenDigits = (val) => str(val).replace(/\D/g, "").slice(-10);

// Lead object (dialer / Leads API) → applicant fields
const mapLeadToApplicant = (lead) => ({
  fullName: str(lead.customerName, lead.name),
  phone: toTenDigits(lead.contactNumber || lead.phone || lead.contact),
  alternatePhone: toTenDigits(lead.alternateNumber),
  email: str(lead.email),
  address: str(
    lead.address,
    [lead.houseNumber, lead.streetName, lead.colonyName, lead.landmark].filter(Boolean).join(", ")
  ),
  city: str(lead.city, lead.district),
  state: str(lead.state),
  pincode: str(lead.areaPinCode, lead.pincode),
});

export default function LoanApplicationForm() {
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const primaryColor = currentTheme?.color || "#2e856e";

  // Dialer "Loan Interested" → ?leadId=  (state.leadData turant milta hai, refresh pe API se)
  const leadIdParam = !isEdit ? searchParams.get("leadId") : null;
  const [linkedLeadId, setLinkedLeadId] = useState(leadIdParam);
  // Lead par pehle se khuli (REJECTED/CLOSED ke alawa) applications — duplicate se bachao
  const [existingLoans, setExistingLoans] = useState([]);

  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [loadingLoan, setLoadingLoan] = useState(isEdit);

  // Form Data
  const [formData, setFormData] = useState({
    // Step 1: Applicant
    fullName: "",
    phone: "",
    alternatePhone: "",
    email: "",
    dob: "",
    gender: "MALE",
    panNumber: "",
    aadhaarNumber: "",
    address: "",
    city: "",
    state: "",
    pincode: "",

    // Step 2: Employment
    employmentType: "SALARIED",
    monthlyIncome: "",
    companyName: "",
    bankAccountNumber: "",
    ifscCode: "",

    // Step 3: Loan & EMI
    loanType: "PERSONAL",
    amount: 500000,
    tenureMonths: 24,
    interestRate: 12.5,
    purpose: "",

    // Step 4: Documents
    documents: {
      aadhaar: null,
      pan: null,
      incomeProof: null,
      bankStatement: null,
      photo: null,
    },
  });

  // Edit mode — existing application load karo (pehle /edit bhi naya loan bana deta tha)
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const raw = await fintechApi.getLoanById(id);
        const loan = raw?.data || raw;
        if (cancelled || !loan) return;
        if (!["DRAFT", "SUBMITTED"].includes(loan.status)) {
          message.warning(`Application is ${loan.status} and can no longer be edited`);
          navigate(`/fintech/loans/${id}`, { replace: true });
          return;
        }
        if (loan.leadId) setLinkedLeadId(String(loan.leadId));
        setFormData((prev) => ({
          ...prev,
          fullName: str(loan.applicantName),
          phone: str(loan.contactNumber, loan.phone),
          alternatePhone: str(loan.alternateNumber, loan.alternatePhone),
          email: str(loan.email),
          dob: str(loan.dateOfBirth, loan.dob).slice(0, 10),
          gender: loan.gender || "MALE",
          panNumber: str(loan.panNumber),
          aadhaarNumber: str(loan.aadhaarNumber),
          address: str(loan.address),
          city: str(loan.city),
          state: str(loan.state),
          pincode: str(loan.pincode),
          employmentType: loan.employmentType || "SALARIED",
          monthlyIncome: str(loan.monthlyIncome),
          companyName: str(loan.companyName),
          bankAccountNumber: str(loan.bankAccountNumber),
          ifscCode: str(loan.ifscCode),
          loanType: loan.loanType || "PERSONAL",
          amount: Number(loan.loanAmount ?? loan.amount) || prev.amount,
          tenureMonths: Number(loan.loanTenureMonths ?? loan.tenure) || prev.tenureMonths,
          interestRate: Number(loan.interestRate) || prev.interestRate,
          purpose: str(loan.purpose),
        }));
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load loan for edit:", err);
        message.error(err.message || "Failed to load loan application");
        navigate("/fintech/loans", { replace: true });
      } finally {
        if (!cancelled) setLoadingLoan(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit, navigate]);

  // Nayi application — lead aur/ya calculator terms se prefill (sirf khaali fields bharo)
  useEffect(() => {
    if (isEdit) return;
    const stateLead = location.state?.leadData;
    const calcTerms = location.state?.prefill;

    const applyPrefill = (patch) =>
      setFormData((prev) => {
        const next = { ...prev };
        Object.entries(patch).forEach(([k, v]) => {
          if (v !== "" && v !== null && v !== undefined) next[k] = v;
        });
        return next;
      });

    if (calcTerms) {
      applyPrefill({
        amount: Number(calcTerms.amount) || undefined,
        tenureMonths: Number(calcTerms.tenureMonths) || undefined,
        interestRate: Number(calcTerms.interestRate) || undefined,
      });
    }

    if (stateLead) {
      applyPrefill(mapLeadToApplicant(stateLead));
      return;
    }
    if (!leadIdParam) return;

    let cancelled = false;
    (async () => {
      try {
        const raw = await leadsApi.getLeadById(leadIdParam);
        const lead = raw?.data || raw;
        if (!cancelled && lead) applyPrefill(mapLeadToApplicant(lead));
      } catch (err) {
        if (cancelled) return;
        console.error("Lead prefill failed:", err);
        message.warning(`Could not load lead #${leadIdParam} details — please fill manually`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, leadIdParam, location.state]);

  // Backend: GET /loans/lead/{leadId} — nayi application se pehle warn karo
  useEffect(() => {
    if (isEdit || !leadIdParam) return;
    let cancelled = false;
    (async () => {
      try {
        const raw = await fintechApi.getLoansByLead(leadIdParam);
        const list = Array.isArray(raw) ? raw : Array.isArray(raw?.content) ? raw.content : Array.isArray(raw?.data) ? raw.data : [];
        const open = list.filter((l) => !["REJECTED", "CLOSED"].includes(String(l.status || "").toUpperCase()));
        if (!cancelled) setExistingLoans(open);
      } catch (err) {
        // Sirf warning hai — fail ho to form chalta rahe
        if (!cancelled) console.warn("Existing loans check failed:", err?.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, leadIdParam]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // EMI Formula: P * R * (1+R)^N / ((1+R)^N - 1)
  const calculatedEmi = useMemo(() => {
    const P = Number(formData.amount) || 0;
    const N = Number(formData.tenureMonths) || 1;
    const annualRate = Number(formData.interestRate) || 12;
    const R = annualRate / (12 * 100); // monthly interest rate

    if (P <= 0 || N <= 0 || R <= 0) return 0;
    const emi = (P * R * Math.pow(1 + R, N)) / (Math.pow(1 + R, N) - 1);
    return Math.round(emi);
  }, [formData.amount, formData.tenureMonths, formData.interestRate]);

  const totalPayable = useMemo(() => {
    return calculatedEmi * (Number(formData.tenureMonths) || 1);
  }, [calculatedEmi, formData.tenureMonths]);

  const totalInterest = useMemo(() => {
    return Math.max(0, totalPayable - (Number(formData.amount) || 0));
  }, [totalPayable, formData.amount]);

  // Step Validation
  const validateStep = (step) => {
    if (step === 1) {
      if (!formData.fullName.trim()) {
        message.warning("Please enter applicant full name");
        return false;
      }
      if (!PHONE_REGEX.test(formData.phone.trim())) {
        message.warning("Please enter valid 10-digit mobile number");
        return false;
      }
      if (formData.alternatePhone && !PHONE_REGEX.test(formData.alternatePhone.trim())) {
        message.warning("Please enter valid alternate mobile number");
        return false;
      }
      if (!PAN_REGEX.test(formData.panNumber.trim())) {
        message.warning("Please enter valid PAN number (e.g. ABCDE1234F)");
        return false;
      }
      if (formData.aadhaarNumber && !/^\d{12}$/.test(formData.aadhaarNumber.trim())) {
        message.warning("Aadhaar number must be 12 digits");
        return false;
      }
      if (formData.pincode && !/^\d{6}$/.test(formData.pincode.trim())) {
        message.warning("Pincode must be 6 digits");
        return false;
      }
      return true;
    }
    if (step === 2) {
      if (!formData.monthlyIncome || Number(formData.monthlyIncome) <= 0) {
        message.warning("Please enter valid monthly income");
        return false;
      }
      if (formData.ifscCode && !IFSC_REGEX.test(formData.ifscCode.trim())) {
        message.warning("Please enter valid IFSC code (e.g. HDFC0001234)");
        return false;
      }
      return true;
    }
    if (step === 3) {
      if (!formData.amount || Number(formData.amount) <= 0) {
        message.warning("Please enter valid loan amount");
        return false;
      }
      return true;
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 5));
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  // Attached files upload karo — jo fail hua uske titles return
  const uploadAttachedDocuments = async (loanId) => {
    const attached = DOCUMENT_TYPES.filter((d) => formData.documents[d.key]);
    const results = await Promise.allSettled(
      attached.map((d) => fintechApi.uploadDocument(loanId, formData.documents[d.key], d.type))
    );
    return attached.filter((_, i) => results[i].status === "rejected").map((d) => d.title);
  };

  const handleSubmit = async () => {
    // Step skip karke submit na ho — pehle 3 steps dobara validate
    for (const step of [1, 2, 3]) {
      if (!validateStep(step)) {
        setCurrentStep(step);
        return;
      }
    }

    // Backend camelCase (Jackson default) — pehle har field camelCase + snake_case dono me jaata tha
    const payload = {
      applicantName: formData.fullName.trim(),
      contactNumber: formData.phone.trim(),
      alternateNumber: formData.alternatePhone.trim() || null,
      email: formData.email.trim() || null,
      dateOfBirth: formData.dob || null,
      gender: formData.gender,
      panNumber: formData.panNumber.trim().toUpperCase(),
      aadhaarNumber: formData.aadhaarNumber.trim() || null,
      address: formData.address.trim() || null,
      city: formData.city.trim() || null,
      state: formData.state.trim() || null,
      pincode: formData.pincode.trim() || null,
      employmentType: formData.employmentType,
      monthlyIncome: Number(formData.monthlyIncome) || 0,
      companyName: formData.companyName.trim() || null,
      bankAccountNumber: formData.bankAccountNumber.trim() || null,
      ifscCode: formData.ifscCode.trim().toUpperCase() || null,
      loanType: formData.loanType,
      loanAmount: Number(formData.amount) || 0,
      loanTenureMonths: Number(formData.tenureMonths) || 12,
      interestRate: Number(formData.interestRate) || 0,
      calculatedEmi,
      purpose: formData.purpose.trim() || null,
      leadId: linkedLeadId || null,
    };

    setSubmitting(true);
    let loanId = isEdit ? id : null;
    try {
      if (isEdit) {
        await fintechApi.updateLoan(id, payload);
      } else {
        const raw = await fintechApi.createLoan(payload);
        const created = raw?.data || raw;
        loanId = created?.id ?? created?.loanId;
        if (!loanId) {
          // Loan ban gaya par id nahi mili — docs/submit nahi ho sakte, duplicate se bachne ke liye list pe bhejo
          message.warning("Application saved, but documents could not be attached (no application id returned).");
          navigate("/fintech/loans");
          return;
        }
      }
    } catch (err) {
      console.error("Loan save error:", err);
      message.error(err.message || "Failed to save loan application");
      setSubmitting(false);
      return;
    }

    // Loan save ho chuka hai — ab koi bhi fail ho to detail page pe bhejo (retry pe duplicate loan na bane)
    try {
      const failedDocs = await uploadAttachedDocuments(loanId);
      if (failedDocs.length) {
        message.warning(`Application saved, but upload failed for: ${failedDocs.join(", ")}. Upload again from the application page.`);
      }
      if (!isEdit) {
        await fintechApi.submitLoan(loanId);
      }
      message.success(isEdit ? "Loan application updated successfully!" : "Loan application submitted successfully!");
    } catch (err) {
      console.error("Loan submit error:", err);
      message.warning(`Application saved as draft, but submission failed: ${err.message || "unknown error"}`);
    } finally {
      setSubmitting(false);
      navigate(`/fintech/loans/${loanId}`);
    }
  };

  if (loadingLoan) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-[#2e856e] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 pb-16">
      {/* ── Page Header ── */}
      <div className="flex items-center justify-between bg-white p-5 rounded-2xl border border-[#e5e7eb] shadow-sm">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(isEdit ? `/fintech/loans/${id}` : "/fintech/loans")}
            className="p-2 hover:bg-gray-100 rounded-xl text-gray-500 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">
              {isEdit ? "Edit Loan Application" : "New Loan Application Wizard"}
            </h1>
            <p className="text-xs md:text-sm text-gray-500">
              Fill in applicant details, loan requirements, and upload KYC papers
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {linkedLeadId && (
            <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 flex items-center gap-1.5">
              <Link2 className="w-3.5 h-3.5" />
              Linked Lead: #{linkedLeadId}
            </span>
          )}
          <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
            Step {currentStep} of 5
          </span>
        </div>
      </div>

      {/* ── Duplicate warning — is lead par application pehle se chal rahi hai ── */}
      {existingLoans.length > 0 && (
        <div className="p-4 bg-amber-50 rounded-2xl border border-amber-200 text-sm text-amber-900 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 flex-shrink-0 text-amber-600 mt-0.5" />
          <div>
            <div className="font-bold">This lead already has {existingLoans.length === 1 ? "an open loan application" : `${existingLoans.length} open loan applications`}</div>
            <div className="mt-1 flex flex-wrap gap-2">
              {existingLoans.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => navigate(`/fintech/loans/${l.id}`)}
                  className="px-2.5 py-1 rounded-lg bg-white border border-amber-300 text-xs font-semibold hover:bg-amber-100"
                >
                  {l.applicationId || `#${l.id}`} · {l.status}
                </button>
              ))}
            </div>
            <div className="text-xs mt-1.5">Continue only if a separate application is genuinely needed.</div>
          </div>
        </div>
      )}

      {/* ── Stepper Navigation ── */}
      <div className="bg-white p-4 rounded-2xl border border-[#e5e7eb] shadow-sm overflow-x-auto">
        <div className="flex items-center justify-between min-w-[600px] px-2">
          {STEPS.map((s, idx) => {
            const Icon = s.icon;
            const isCompleted = currentStep > s.step;
            const isCurrent = currentStep === s.step;

            return (
              <React.Fragment key={s.step}>
                <div
                  onClick={() => isCompleted && setCurrentStep(s.step)}
                  className={`flex items-center gap-2 cursor-pointer transition-all ${
                    isCompleted ? "opacity-90" : isCurrent ? "opacity-100 font-bold" : "opacity-40"
                  }`}
                >
                  <div
                    className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold transition-all shadow-sm ${
                      isCurrent
                        ? "text-white ring-4 ring-[#2e856e]/20"
                        : isCompleted
                        ? "bg-emerald-100 text-emerald-800"
                        : "bg-gray-100 text-gray-500"
                    }`}
                    style={isCurrent ? { backgroundColor: primaryColor } : {}}
                  >
                    {isCompleted ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  </div>
                  <div className="text-left">
                    <div className="text-[10px] uppercase font-bold text-gray-400">
                      Step {s.step}
                    </div>
                    <div
                      className={`text-xs whitespace-nowrap ${
                        isCurrent ? "font-bold text-gray-900" : "font-medium text-gray-600"
                      }`}
                    >
                      {s.title}
                    </div>
                  </div>
                </div>

                {idx < STEPS.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-3 rounded transition-colors ${
                      currentStep > idx + 1 ? "bg-emerald-500" : "bg-gray-200"
                    }`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* ── Form Body Containers ── */}
      <div className="bg-white p-6 md:p-8 rounded-2xl border border-[#e5e7eb] shadow-sm">
        {/* ── Step 1: Applicant Details ── */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <h2 className="text-base font-bold text-gray-900 flex items-center gap-2 border-b pb-3">
              <User className="w-5 h-5 text-[#2e856e]" />
              <span>Personal & Contact Information</span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Full Name (as per PAN) *
                </label>
                <input
                  type="text"
                  placeholder="e.g. Rahul Sharma"
                  value={formData.fullName}
                  onChange={(e) => handleChange("fullName", e.target.value)}
                  className="w-full px-3.5 py-2.5 text-sm bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#2e856e]/20 focus:border-[#2e856e]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Mobile Number *
                </label>
                <input
                  type="tel"
                  maxLength={10}
                  placeholder="e.g. 9876543210"
                  value={formData.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  className="w-full px-3.5 py-2.5 text-sm bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#2e856e]/20 focus:border-[#2e856e]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Alternate Phone
                </label>
                <input
                  type="tel"
                  maxLength={10}
                  placeholder="Optional"
                  value={formData.alternatePhone}
                  onChange={(e) => handleChange("alternatePhone", e.target.value)}
                  className="w-full px-3.5 py-2.5 text-sm bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#2e856e]/20 focus:border-[#2e856e]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  placeholder="e.g. rahul@example.com"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  className="w-full px-3.5 py-2.5 text-sm bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#2e856e]/20 focus:border-[#2e856e]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Date of Birth
                </label>
                <input
                  type="date"
                  value={formData.dob}
                  onChange={(e) => handleChange("dob", e.target.value)}
                  className="w-full px-3.5 py-2.5 text-sm bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#2e856e]/20 focus:border-[#2e856e]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Gender</label>
                <select
                  value={formData.gender}
                  onChange={(e) => handleChange("gender", e.target.value)}
                  className="w-full px-3.5 py-2.5 text-sm bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#2e856e]/20 focus:border-[#2e856e]"
                >
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  PAN Number *
                </label>
                <input
                  type="text"
                  maxLength={10}
                  placeholder="e.g. ABCDE1234F"
                  value={formData.panNumber}
                  onChange={(e) => handleChange("panNumber", e.target.value.toUpperCase())}
                  className="w-full px-3.5 py-2.5 text-sm font-mono uppercase bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#2e856e]/20 focus:border-[#2e856e]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Aadhaar Number (12 Digits)
                </label>
                <input
                  type="text"
                  maxLength={12}
                  placeholder="e.g. 123456789012"
                  value={formData.aadhaarNumber}
                  onChange={(e) => handleChange("aadhaarNumber", e.target.value)}
                  className="w-full px-3.5 py-2.5 text-sm font-mono bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#2e856e]/20 focus:border-[#2e856e]"
                />
              </div>
            </div>

            {/* Address */}
            <div className="space-y-3 pt-2">
              <label className="block text-xs font-bold text-gray-700">Residential Address</label>
              <input
                type="text"
                placeholder="House No., Street, Landmark"
                value={formData.address}
                onChange={(e) => handleChange("address", e.target.value)}
                className="w-full px-3.5 py-2.5 text-sm bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#2e856e]/20 focus:border-[#2e856e]"
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                  type="text"
                  placeholder="City"
                  value={formData.city}
                  onChange={(e) => handleChange("city", e.target.value)}
                  className="w-full px-3.5 py-2 text-sm bg-gray-50 rounded-xl border border-gray-200 focus:outline-none"
                />
                <input
                  type="text"
                  placeholder="State"
                  value={formData.state}
                  onChange={(e) => handleChange("state", e.target.value)}
                  className="w-full px-3.5 py-2 text-sm bg-gray-50 rounded-xl border border-gray-200 focus:outline-none"
                />
                <input
                  type="text"
                  maxLength={6}
                  placeholder="Pincode"
                  value={formData.pincode}
                  onChange={(e) => handleChange("pincode", e.target.value)}
                  className="w-full px-3.5 py-2 text-sm bg-gray-50 rounded-xl border border-gray-200 focus:outline-none"
                />
              </div>
            </div>
          </div>
        )}

        {/* ── Step 2: Employment & Income ── */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <h2 className="text-base font-bold text-gray-900 flex items-center gap-2 border-b pb-3">
              <Briefcase className="w-5 h-5 text-[#2e856e]" />
              <span>Employment & Banking Information</span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Employment Type *
                </label>
                <select
                  value={formData.employmentType}
                  onChange={(e) => handleChange("employmentType", e.target.value)}
                  className="w-full px-3.5 py-2.5 text-sm bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#2e856e]/20 focus:border-[#2e856e]"
                >
                  <option value="SALARIED">Salaried Employee</option>
                  <option value="SELF_EMPLOYED">Self-Employed Professional</option>
                  <option value="BUSINESS">Business Owner / Trader</option>
                  <option value="FARMER">Farmer / Agriculture</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Monthly Net Income (₹) *
                </label>
                <input
                  type="number"
                  placeholder="e.g. 50000"
                  value={formData.monthlyIncome}
                  onChange={(e) => handleChange("monthlyIncome", e.target.value)}
                  className="w-full px-3.5 py-2.5 text-sm bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#2e856e]/20 focus:border-[#2e856e]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Company / Firm Name
                </label>
                <input
                  type="text"
                  placeholder="e.g. TCS / Self Firm"
                  value={formData.companyName}
                  onChange={(e) => handleChange("companyName", e.target.value)}
                  className="w-full px-3.5 py-2.5 text-sm bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#2e856e]/20 focus:border-[#2e856e]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Disbursal Bank Account Number
                </label>
                <input
                  type="text"
                  placeholder="Bank Account Number"
                  value={formData.bankAccountNumber}
                  onChange={(e) => handleChange("bankAccountNumber", e.target.value)}
                  className="w-full px-3.5 py-2.5 text-sm font-mono bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#2e856e]/20 focus:border-[#2e856e]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Bank IFSC Code
                </label>
                <input
                  type="text"
                  maxLength={11}
                  placeholder="e.g. HDFC0001234"
                  value={formData.ifscCode}
                  onChange={(e) => handleChange("ifscCode", e.target.value.toUpperCase())}
                  className="w-full px-3.5 py-2.5 text-sm font-mono uppercase bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#2e856e]/20 focus:border-[#2e856e]"
                />
              </div>
            </div>
          </div>
        )}

        {/* ── Step 3: Loan Details & Live EMI ── */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <h2 className="text-base font-bold text-gray-900 flex items-center gap-2 border-b pb-3">
              <Wallet className="w-5 h-5 text-[#2e856e]" />
              <span>Loan Configuration & Live EMI Calculator</span>
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Left Column: Form Controls */}
              <div className="lg:col-span-7 space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Select Loan Product
                  </label>
                  <select
                    value={formData.loanType}
                    onChange={(e) => {
                      const selected = LOAN_TYPES.find((p) => p.id === e.target.value);
                      handleChange("loanType", e.target.value);
                      if (selected) handleChange("interestRate", selected.defaultRate);
                    }}
                    className="w-full px-3.5 py-2.5 text-sm bg-gray-50 rounded-xl border border-gray-200 focus:outline-none font-semibold text-gray-800"
                  >
                    {LOAN_TYPES.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.label} (Base Rate: {t.defaultRate}%)
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="text-xs font-bold text-gray-700">
                      Loan Amount (₹)
                    </label>
                    <span className="text-sm font-black text-[#2e856e]">
                      {formatINR(formData.amount)}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={25000}
                    max={5000000}
                    step={25000}
                    value={formData.amount}
                    onChange={(e) => handleChange("amount", Number(e.target.value))}
                    className="w-full accent-[#2e856e] cursor-pointer"
                  />
                  <input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => handleChange("amount", Number(e.target.value))}
                    className="mt-2 w-full px-3 py-1.5 text-sm bg-gray-50 rounded-xl border border-gray-200"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      Tenure (Months)
                    </label>
                    <select
                      value={formData.tenureMonths}
                      onChange={(e) => handleChange("tenureMonths", Number(e.target.value))}
                      className="w-full px-3.5 py-2 text-sm bg-gray-50 rounded-xl border border-gray-200 font-semibold"
                    >
                      {/* Calculator / existing loan se non-standard tenure aaye to bhi option dikhe */}
                      {!STANDARD_TENURES.includes(Number(formData.tenureMonths)) && (
                        <option value={formData.tenureMonths}>{formData.tenureMonths} Months</option>
                      )}
                      <option value={6}>6 Months</option>
                      <option value={12}>12 Months (1 Yr)</option>
                      <option value={24}>24 Months (2 Yrs)</option>
                      <option value={36}>36 Months (3 Yrs)</option>
                      <option value={48}>48 Months (4 Yrs)</option>
                      <option value={60}>60 Months (5 Yrs)</option>
                      <option value={120}>120 Months (10 Yrs)</option>
                      <option value={240}>240 Months (20 Yrs)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      Interest Rate (% p.a.)
                    </label>
                    <input
                      type="number"
                      step={0.1}
                      value={formData.interestRate}
                      onChange={(e) => handleChange("interestRate", Number(e.target.value))}
                      className="w-full px-3.5 py-2 text-sm bg-gray-50 rounded-xl border border-gray-200 font-semibold"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Purpose of Loan
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Working Capital / Medical / Home Renovation"
                    value={formData.purpose}
                    onChange={(e) => handleChange("purpose", e.target.value)}
                    className="w-full px-3.5 py-2 text-sm bg-gray-50 rounded-xl border border-gray-200"
                  />
                </div>
              </div>

              {/* Right Column: Live EMI Result Card */}
              <div className="lg:col-span-5 bg-gradient-to-br from-[#2e856e]/10 via-emerald-50/50 to-white p-6 rounded-2xl border border-emerald-200/80 shadow-sm flex flex-col justify-between">
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-[#2e856e]">
                    Live EMI Preview
                  </span>
                  <div className="text-3xl font-black text-gray-900 mt-2">
                    {formatINR(calculatedEmi)}{" "}
                    <span className="text-xs font-medium text-gray-500">/ month</span>
                  </div>
                </div>

                <div className="space-y-2.5 my-4 text-xs border-y border-emerald-200/60 py-4">
                  <div className="flex justify-between text-gray-600">
                    <span>Principal Amount:</span>
                    <strong className="text-gray-900">{formatINR(formData.amount)}</strong>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Total Interest:</span>
                    <strong className="text-emerald-800">{formatINR(totalInterest)}</strong>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Total Payable Amount:</span>
                    <strong className="text-gray-900">{formatINR(totalPayable)}</strong>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Tenure:</span>
                    <strong className="text-gray-900">{formData.tenureMonths} Months</strong>
                  </div>
                </div>

                <div className="text-[11px] text-gray-500 bg-white/70 p-2.5 rounded-xl border border-emerald-100">
                  ⚡ Auto-computed via reducing balance interest method.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Step 4: Documents Upload ── */}
        {currentStep === 4 && (
          <div className="space-y-6">
            <h2 className="text-base font-bold text-gray-900 flex items-center gap-2 border-b pb-3">
              <FileCheck className="w-5 h-5 text-[#2e856e]" />
              <span>Document Vault & Verification Papers</span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {DOCUMENT_TYPES.map((doc) => {
                const isUploaded = !!formData.documents[doc.key];
                return (
                  <div
                    key={doc.key}
                    className={`p-4 rounded-xl border transition-all flex items-center justify-between ${
                      isUploaded
                        ? "bg-emerald-50/60 border-emerald-300"
                        : "bg-gray-50/70 border-gray-200"
                    }`}
                  >
                    <div>
                      <div className="font-bold text-sm text-gray-900 flex items-center gap-2">
                        <span>{doc.title}</span>
                        {isUploaded && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                            Uploaded
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{doc.desc}</p>
                    </div>

                    <AntUpload
                      accept=".pdf,.jpg,.jpeg,.png"
                      beforeUpload={(file) => {
                        if (file.size > 10 * 1024 * 1024) {
                          message.error(`${doc.title}: file must be under 10MB`);
                          return AntUpload.LIST_IGNORE;
                        }
                        setFormData((prev) => ({
                          ...prev,
                          documents: { ...prev.documents, [doc.key]: file },
                        }));
                        message.success(`${doc.title} attached — uploads on submit`);
                        return false;
                      }}
                      showUploadList={false}
                    >
                      <Button
                        icon={<Upload className="w-4 h-4" />}
                        className="rounded-xl text-xs font-semibold"
                      >
                        {isUploaded ? "Replace" : "Upload"}
                      </Button>
                    </AntUpload>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Step 5: Review & Submit ── */}
        {currentStep === 5 && (
          <div className="space-y-6">
            <h2 className="text-base font-bold text-gray-900 flex items-center gap-2 border-b pb-3">
              <CheckCircle2 className="w-5 h-5 text-[#2e856e]" />
              <span>Review Application Summary</span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
              {/* Box 1 */}
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-2">
                <div className="font-bold text-sm text-gray-800 border-b pb-1">
                  Applicant Info
                </div>
                <div>Name: <strong>{formData.fullName || "-"}</strong></div>
                <div>Phone: <strong>{formData.phone || "-"}</strong></div>
                <div>PAN: <strong className="font-mono">{formData.panNumber || "-"}</strong></div>
                <div>City: <strong>{formData.city || "-"}</strong></div>
              </div>

              {/* Box 2 */}
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-2">
                <div className="font-bold text-sm text-gray-800 border-b pb-1">
                  Employment & Bank
                </div>
                <div>Type: <strong>{formData.employmentType}</strong></div>
                <div>Monthly Income: <strong>{formatINR(formData.monthlyIncome)}</strong></div>
                <div>Bank A/C: <strong className="font-mono">{formData.bankAccountNumber || "Pending"}</strong></div>
                <div>IFSC: <strong className="font-mono">{formData.ifscCode || "Pending"}</strong></div>
              </div>

              {/* Box 3 */}
              <div className="p-4 bg-emerald-50/60 rounded-xl border border-emerald-200 space-y-2">
                <div className="font-bold text-sm text-emerald-900 border-b border-emerald-200 pb-1">
                  Loan Details
                </div>
                <div>Product: <strong>{formData.loanType}</strong></div>
                <div>Amount: <strong className="text-emerald-800 font-black">{formatINR(formData.amount)}</strong></div>
                <div>Tenure: <strong>{formData.tenureMonths} Months</strong></div>
                <div>Monthly EMI: <strong className="text-emerald-900 font-black">{formatINR(calculatedEmi)}</strong></div>
              </div>
            </div>

            <div className="p-4 bg-amber-50 rounded-xl border border-amber-200 text-xs text-amber-900 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 flex-shrink-0 text-amber-600" />
              <span>
                {isEdit ? (
                  <>Saving will update this application. Newly attached documents will be uploaded.</>
                ) : (
                  <>By submitting, this loan file will enter the <strong>SUBMITTED</strong> state and will be assigned to credit underwriters for KYC and credit appraisal.</>
                )}
              </span>
            </div>
          </div>
        )}

        {/* ── Wizard Controls Footer ── */}
        <div className="flex items-center justify-between pt-6 mt-6 border-t border-gray-100">
          <button
            type="button"
            onClick={prevStep}
            disabled={currentStep === 1}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold border transition-all ${
              currentStep === 1
                ? "opacity-40 cursor-not-allowed bg-gray-50 text-gray-400 border-gray-200"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            }`}
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Previous</span>
          </button>

          {currentStep < 5 ? (
            <button
              type="button"
              onClick={nextStep}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-white shadow-md transition-all hover:brightness-105 active:scale-95"
              style={{ backgroundColor: primaryColor }}
            >
              <span>Next Step</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="flex items-center gap-2 px-8 py-2.5 rounded-xl text-sm font-bold text-white shadow-lg transition-all hover:brightness-110 active:scale-95"
              style={{ backgroundColor: primaryColor }}
            >
              {submitting ? (
                <span>{isEdit ? "Saving..." : "Submitting..."}</span>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  <span>{isEdit ? "Save Changes" : "Submit Loan Application"}</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
